import React, { useState, useEffect } from 'react';
import { UserRole } from '../types/task';
import { useAuth } from '../hooks/useAuth';
import { getAuthService } from '../services/authService';
import { setupSharePointForTasks, testSharePointConnection } from '../services/sharepointHelpers';
import TaskDashboard from '../components/TaskDashboard';

const TaskManagement: React.FC = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const [userRole, setUserRole] = useState<UserRole>(UserRole.STAFF);
  const [userId, setUserId] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [sharePointStatus, setSharePointStatus] = useState<'idle' | 'testing' | 'connected' | 'error'>('idle');
  const [sharePointError, setSharePointError] = useState<string>('');

  useEffect(() => {
    if (user) {
      // Map auth UserRole to task UserRole enum
      const mapAuthRoleToTaskRole = (authRole: string): UserRole => {
        // Map auth roles to task management roles
        if (authRole === 'admin' || authRole === 'executive') {
          return UserRole.EXECUTIVE;
        } else if (authRole === 'manager') {
          return UserRole.MANAGER;
        } else {
          return UserRole.STAFF;
        }
      };

      const role = mapAuthRoleToTaskRole(user.role || 'volunteer');
      setUserRole(role);
      setUserId(user.id || user.email || '');
      setUserName(user.name || user.email || '');
      
      // Test SharePoint connection when user is authenticated
      testSharePointConnectionOnLoad();
    }
  }, [user]);

  // Test SharePoint connection on component load
  const testSharePointConnectionOnLoad = async () => {
    try {
      setSharePointStatus('testing');
      setSharePointError('');
      
      const authService = getAuthService();
      const isConnected = await testSharePointConnection(authService);
      
      if (isConnected) {
        setSharePointStatus('connected');
        console.log('✅ SharePoint connection verified on page load');
      } else {
        setSharePointStatus('error');
        setSharePointError('Failed to connect to SharePoint');
      }
    } catch (error) {
      console.error('❌ Error testing SharePoint connection:', error);
      setSharePointStatus('error');
      setSharePointError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  // Setup SharePoint lists
  const handleSetupSharePoint = async () => {
    try {
      setSharePointStatus('testing');
      setSharePointError('');
      
      const authService = getAuthService();
      await setupSharePointForTasks(authService);
      
      setSharePointStatus('connected');
      console.log('✅ SharePoint setup completed');
    } catch (error) {
      console.error('❌ Error setting up SharePoint:', error);
      setSharePointStatus('error');
      setSharePointError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please sign in to access the Task Management Portal.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SharePoint Status Banner */}
      {sharePointStatus !== 'idle' && (
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                sharePointStatus === 'connected' ? 'bg-green-500' :
                sharePointStatus === 'testing' ? 'bg-yellow-500' :
                'bg-red-500'
              }`}></div>
              <span className="text-sm font-medium">
                SharePoint Status: {
                  sharePointStatus === 'connected' ? 'Connected' :
                  sharePointStatus === 'testing' ? 'Testing Connection...' :
                  'Connection Error'
                }
              </span>
              {sharePointError && (
                <span className="text-sm text-red-600">({sharePointError})</span>
              )}
            </div>
            {sharePointStatus === 'error' && (
              <button
                onClick={handleSetupSharePoint}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Setup SharePoint
              </button>
            )}
          </div>
        </div>
      )}
      
      <TaskDashboard
        userRole={userRole}
        userId={userId}
        userName={userName}
      />
    </div>
  );
};

export default TaskManagement; 